import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Login from "./components/Login";
import EmailVerification from "./components/EmailVerification";
import PhoneVerification from "./components/PhoneVerification";
import FirstTimePasswordReset from "./components/FirstTimePasswordReset";
import Dashboard from "./components/Dashboard";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { useActivityMonitor } from "./hooks/useActivityMonitor";
import { useAccessControl } from "./hooks/useAccessControl";
import { RESOURCES, ACTIONS } from "./hooks/usePermissions";
import { useFirstAccessibleRoute } from "./hooks/useFirstAccessibleRoute";
import NotFoundPage from "./components/common/NotFoundPage";

import Students from "./components/student/Students";
import StudentLedger from "./components/student/StudentLedger";
import StudentPromotionPage from "./components/student/StudentPromotionPage";
import StudentBulkStatusPage from "./components/student/StudentBulkStatusPage";

import FeeTypes from "./components/fees/FeeTypes";
import FeeGroups from "./components/fees/FeeGroups";
import FeeAllocate from "./components/fees/FeeAllocate";
import FeePayments from "./components/fees/FeePayments";
import ClearanceSettings from "./components/fees/ClearanceSettings";
import ClearanceCardGeneration from "./components/fees/ClearanceCardGeneration";

import Reports from "./components/fees/Reports";
import Transactions from "./components/accounting/Transactions";
import Accounts from "./components/accounting/Accounts";
import VoucherHeads from "./components/accounting/VoucherHeads";
import EmailLogs from "./components/communications/EmailLogs";
import EmailManagement from "./components/communications/EmailManagement";
import Communications from "./components/communications/Communications";
import SMSPage from "./components/communications/SMSPage";
import Teachers from "./components/teachers/Teachers";
import ClassAllocations from "./components/class-allocations/ClassAllocations";
import SubjectAllocations from "./components/subject-allocations/SubjectAllocations";
import AcademicClasses from "./components/academic/AcademicClasses";
import AcademicTerms from "./components/academic/AcademicTerms";
import AcademicLevels from "./components/academic/AcademicLevels";
import Subjects from "./components/academic/Subjects";
import SubjectPapers from "./components/academic/SubjectPapers";
import SubjectWithPapers from "./components/academic/SubjectWithPapers";
import ClassStreamSubjectConfig from "./components/academic/ClassStreamSubjectConfig";
import RBACPage from "./components/rbac/RBACPage";
import UserManagement from "./components/settings/UserManagement";
import AccountSettings from "./components/settings/AccountSettings";
import Roles from "./components/settings/Roles";
import SchoolSettings from "./components/settings/SchoolSettings";
import AssessmentTypes from "./components/assessments/AssessmentTypes";
import AssessmentConfigurations from "./components/assessments/AssessmentConfigurations";
import StudentMarks from "./components/assessments/StudentMarks";
import AssessmentReports from "./components/assessments/AssessmentReports";
import TeacherWorkload from "./components/assessments/TeacherWorkload";
import GradingScales from "./components/grading/GradingScales";
import GradeBoundaries from "./components/grading/GradeBoundaries";
import ClassGradingScales from "./components/grading/ClassGradingScales";
import ReportCardHub from "./components/report-card/ReportCardHub";
import ConductManagement from "./components/report-card/ConductManagement";
import CommentsManagement from "./components/report-card/CommentsManagement";
import ConductCategories from "./components/report-card/ConductCategories";
import ConductRatings from "./components/report-card/ConductRatings";
import StudentConduct from "./components/report-card/StudentConduct";
import SubjectComments from "./components/report-card/SubjectComments";
import GeneralComments from "./components/report-card/GeneralComments";
import ReportCardGeneration from "./components/report-card/ReportCardGeneration";
import CommentTemplates from "./components/report-card/CommentTemplates";
import ReportCardTemplates from "./components/report-card/ReportCardTemplates";
// TODO: Uncomment when PrimaryPreferences component is developed
// import PrimaryPreferences from "./components/report-card/PrimaryPreferences";
import Layout from "./components/Layout";
import StudentLogin from "./components/student-portal/StudentLogin";
import StudentDashboard from "./components/student-portal/StudentDashboard";
import StudentChangePassword from "./components/student-portal/StudentChangePassword";
import { StudentAuthProvider, useStudentAuth } from "./hooks/useStudentAuth";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2e7d32",
    },
    secondary: {
      main: "#f50057",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: '"Avenir LT Pro", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
    h1: {
      fontSize: "1.8rem",
      "@media (max-width:600px)": { fontSize: "1.4rem" },
    },
    h2: {
      fontSize: "1.6rem",
      "@media (max-width:600px)": { fontSize: "1.2rem" },
    },
    h3: {
      fontSize: "1.4rem",
      "@media (max-width:600px)": { fontSize: "1.1rem" },
    },
    h4: {
      fontSize: "1.2rem",
      "@media (max-width:600px)": { fontSize: "1rem" },
    },
    h5: {
      fontSize: "1rem",
      "@media (max-width:600px)": { fontSize: "0.9rem" },
    },
    h6: {
      fontSize: "0.9rem",
      "@media (max-width:600px)": { fontSize: "0.8rem" },
    },
    body1: {
      fontSize: "0.8rem",
      "@media (max-width:600px)": { fontSize: "0.75rem" },
    },
    body2: {
      fontSize: "0.75rem",
      "@media (max-width:600px)": { fontSize: "0.7rem" },
    },
    button: {
      fontSize: "0.8rem",
      "@media (max-width:600px)": { fontSize: "0.75rem" },
    },
    caption: {
      fontSize: "0.7rem",
      "@media (max-width:600px)": { fontSize: "0.65rem" },
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Avenir LT Pro", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: "xl",
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          fontFamily: '"Avenir LT Pro", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          "@media (max-width:600px)": {
            margin: "0 4px",
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          fontFamily: '"Avenir LT Pro", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          "@media (max-width:900px)": {
            overflowX: "auto",
            "& .MuiTable-root": {
              minWidth: 650,
            },
          },
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({
  children,
  requiredPermission = null,
  requiredPermissions = [],
  requireAll = true,
}) => {
  const { user, loading, canAccess, canAny, canAll } = useAccessControl();
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && !canAccess(requiredPermission)) {
    return <Navigate to="/404" replace />;
  }

  if (requiredPermissions.length > 0) {
    const isAllowed = requireAll ? canAll(requiredPermissions) : canAny(requiredPermissions);
    if (!isAllowed) {
      return <Navigate to="/404" replace />;
    }
  }
  
  return children;
};

// Dashboard guard — redirects to first accessible route if no dashboard read
const DashboardRoute = () => {
  const { can, loading } = useAccessControl();
  const firstRoute = useFirstAccessibleRoute();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!can(RESOURCES.DASHBOARD, ACTIONS.READ)) {
    return <Navigate to={firstRoute} replace />;
  }

  return (
    <LayoutWrapper>
      <Dashboard />
    </LayoutWrapper>
  );
};

const LayoutWrapper = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Map routes to page names for the sidebar
  const getPageFromPath = (path) => {
    const routeMap = {
      "/": "dashboard",
      "/students": "students",
      "/teachers": "teachers",
      "/fees/types": "fees-types",
      "/fees/groups": "fees-groups",
      "/fees/allocate": "fees-allocate",
      "/fees/payments": "fees-payments",
      "/fees/reports": "fees-reports",
      "/fees/clearance-settings": "fees-clearance-settings",
      "/fees/clearance-cards": "fees-clearance-cards",
      "/accounting/transactions": "accounting-transactions",
      "/accounting/accounts": "accounting-accounts",
      "/accounting/voucher-heads": "accounting-voucher-heads",
      "/academic/classes": "academic-classes",
      "/academic/terms": "academic-terms",
      "/academic/levels": "academic-levels",
      "/academic/subjects": "academic-subjects",
      "/academic/subject-with-papers": "academic-subject-with-papers",
      "/academic/subject-papers": "academic-subject-papers",
      "/academic/curriculum": "academic-curriculum",
      "/academic/class-allocations": "academic-class-allocations",
      "/academic/subject-allocations": "academic-subject-allocations",
      "/communications": "communications",
      "/communications/logs": "communications-logs",
      "/communications/emails": "communications-email-management",
      "/communications/sms": "communications-sms",
      "/teachers": "teachers",
      "/teachers/class-allocations": "teachers-class-allocations",
      "/teachers/subject-allocations": "teachers-subject-allocations",
      "/settings/users": "settings-users",
      "/settings/roles": "settings-roles",
      "/settings/school": "settings-school",
      "/settings/account": "settings-account",
      "/settings/rbac": "settings-rbac",
      "/assessments/types": "assessments-types",
      "/assessments/configurations": "assessments-configurations",
      "/assessments/marks": "assessments-marks",
      "/assessments/reports": "assessments-reports",
      "/assessments/teacher-workload": "assessments-teacher-workload",
      "/grading/scales": "grading-scales",
      "/grading/boundaries": "grading-boundaries",
      "/grading/class-scales": "grading-class-scales",
      "/report-card/hub": "report-card-hub",
      "/report-card/conduct": "report-card-conduct",
      "/report-card/comments": "report-card-comments",
      "/report-card/conduct-categories": "report-card-conduct-categories",
      "/report-card/conduct-ratings": "report-card-conduct-ratings",
      "/report-card/student-conduct": "report-card-student-conduct",
      "/report-card/subject-comments": "report-card-subject-comments",
      "/report-card/general-comments": "report-card-general-comments",
      "/report-card/generation": "report-card-generation",
      "/report-card/comment-templates": "report-card-comment-templates",
      "/report-card/templates": "report-card-templates",
      // "/report-card/primary-preferences": "report-card-primary-preferences", // TODO: Uncomment when PrimaryPreferences component is developed
    };
    return routeMap[path] || "dashboard";
  };

  const handlePageChange = (pageName) => {
    const pageMap = {
      dashboard: "/",
      students: "/students",
      teachers: "/teachers",
      "fees-types": "/fees/types",
      "fees-groups": "/fees/groups",
      "fees-allocate": "/fees/allocate",
      "fees-payments": "/fees/payments",
      "fees-reports": "/fees/reports",
      "fees-clearance-settings": "/fees/clearance-settings",
      "fees-clearance-cards": "/fees/clearance-cards",
      "accounting-transactions": "/accounting/transactions",
      "accounting-accounts": "/accounting/accounts",
      "accounting-voucher-heads": "/accounting/voucher-heads",
      "academic-classes": "/academic/classes",
      "academic-terms": "/academic/terms",
      "academic-levels": "/academic/levels",
      "academic-subjects": "/academic/subjects",
      "academic-subject-with-papers": "/academic/subject-with-papers",
      "academic-subject-papers": "/academic/subject-papers",
      "academic-curriculum": "/academic/curriculum",
      "academic-class-allocations": "/academic/class-allocations",
      "academic-subject-allocations": "/academic/subject-allocations",
      communications: "/communications",
      "communications-logs": "/communications/logs",
      "communications-email-management": "/communications/emails",
      "communications-sms": "/communications/sms",
      teachers: "/teachers",
      "teachers-class-allocations": "/teachers/class-allocations",
      "teachers-subject-allocations": "/teachers/subject-allocations",
      "settings-users": "/settings/users",
      "settings-roles": "/settings/roles",
      "settings-school": "/settings/school",
      "settings-account": "/settings/account",
      "settings-rbac": "/settings/rbac",
      "assessments-types": "/assessments/types",
      "assessments-configurations": "/assessments/configurations",
      "assessments-marks": "/assessments/marks",
      "assessments-reports": "/assessments/reports",
      "assessments-teacher-workload": "/assessments/teacher-workload",
      "grading-scales": "/grading/scales",
      "grading-boundaries": "/grading/boundaries",
      "grading-class-scales": "/grading/class-scales",
      "report-card-hub": "/report-card/hub",
      "report-card-conduct": "/report-card/conduct",
      "report-card-comments": "/report-card/comments",
      "report-card-conduct-categories": "/report-card/conduct-categories",
      "report-card-conduct-ratings": "/report-card/conduct-ratings",
      "report-card-student-conduct": "/report-card/student-conduct",
      "report-card-subject-comments": "/report-card/subject-comments",
      "report-card-general-comments": "/report-card/general-comments",
      "report-card-generation": "/report-card/generation",
      "report-card-comment-templates": "/report-card/comment-templates",
      "report-card-templates": "/report-card/templates",
      // "report-card-primary-preferences": "/report-card/primary-preferences", // TODO: Uncomment when PrimaryPreferences component is developed
    };
    navigate(pageMap[pageName] || "/");
  };

  return (
    <Layout
      user={user}
      onLogout={logout}
      currentPage={getPageFromPath(window.location.pathname)}
      setCurrentPage={handlePageChange}
    >
      {children}
    </Layout>
  );
};

function AppContent() {
  const { user, loading, login: authLogin, logout } = useAuth();
  const navigate = useNavigate();
  
  useActivityMonitor();

  const handleLogin = (userData) => {
    authLogin(userData.user, userData.token, userData.permissions, userData.expires_at);
    navigate('/');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
        }
      />
      
      <Route
        path="/verify-email"
        element={<EmailVerification onLogin={handleLogin} />}
      />
      
      <Route
        path="/verify-phone"
        element={<PhoneVerification onLogin={handleLogin} />}
      />
      
      <Route
        path="/first-time-reset"
        element={<FirstTimePasswordReset onPasswordReset={handleLogin} />}
      />

      <Route path="/404" element={<NotFoundPage />} />

      {/* Student Portal */}
      <Route path="/student/login" element={<StudentLogin />} />
      <Route path="/student/dashboard" element={<StudentProtectedRoute><StudentDashboard /></StudentProtectedRoute>} />
      <Route path="/student/change-password" element={<StudentProtectedRoute><StudentChangePassword /></StudentProtectedRoute>} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardRoute />
          </ProtectedRoute>
        }
      />

      <Route path="/students" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.STUDENTS, action: ACTIONS.READ }}><LayoutWrapper><Students /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/students/promote" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.STUDENTS, action: ACTIONS.UPDATE }}><LayoutWrapper><StudentPromotionPage /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/students/deactivate" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.STUDENTS, action: ACTIONS.UPDATE }}><LayoutWrapper><StudentBulkStatusPage /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/students/ledger/:studentId" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.STUDENTS, action: ACTIONS.READ }}><LayoutWrapper><StudentLedger /></LayoutWrapper></ProtectedRoute>} />

      <Route path="/fees/types" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.TYPES, action: ACTIONS.READ }}><LayoutWrapper><FeeTypes /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/groups" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.GROUPS, action: ACTIONS.READ }}><LayoutWrapper><FeeGroups /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/allocate" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.ALLOCATIONS, action: ACTIONS.READ }}><LayoutWrapper><FeeAllocate /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/payments" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.PAYMENTS, action: ACTIONS.READ }}><LayoutWrapper><FeePayments /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/reports" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.REPORTS, action: ACTIONS.READ }}><LayoutWrapper><Reports /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/clearance-settings" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.CLEARANCE, action: ACTIONS.READ }}><LayoutWrapper><ClearanceSettings /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/clearance-cards" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.CLEARANCE, action: ACTIONS.READ }}><LayoutWrapper><ClearanceCardGeneration /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/accounting/transactions" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACCOUNTING.TRANSACTIONS, action: ACTIONS.READ }}><LayoutWrapper><Transactions /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/accounting/accounts" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACCOUNTING.ACCOUNTS, action: ACTIONS.READ }}><LayoutWrapper><Accounts /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/accounting/voucher-heads" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACCOUNTING.VOUCHERS, action: ACTIONS.READ }}><LayoutWrapper><VoucherHeads /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/classes" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACADEMIC.CLASSES, action: ACTIONS.READ }}><LayoutWrapper><AcademicClasses /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/terms" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACADEMIC.TERMS, action: ACTIONS.READ }}><LayoutWrapper><AcademicTerms /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/levels" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACADEMIC.LEVELS, action: ACTIONS.READ }}><LayoutWrapper><AcademicLevels /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/subjects" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECTS, action: ACTIONS.READ }}><LayoutWrapper><Subjects /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/subject-papers" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECT_PAPERS, action: ACTIONS.READ }}><LayoutWrapper><SubjectPapers /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/subject-with-papers" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECT_PAPERS, action: ACTIONS.READ }}><LayoutWrapper><SubjectWithPapers /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/curriculum" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CLASS_STREAM_PAPERS, action: ACTIONS.READ }}><LayoutWrapper><ClassStreamSubjectConfig /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/class-allocations" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CLASS_TEACHER_ALLOCATIONS, action: ACTIONS.READ }}><LayoutWrapper><ClassAllocations /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/subject-allocations" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECT_TEACHER_ALLOCATIONS, action: ACTIONS.READ }}><LayoutWrapper><SubjectAllocations /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/communications" element={<ProtectedRoute requiredPermissions={[{ resource: RESOURCES.COMMUNICATIONS.EMAIL, action: ACTIONS.READ }, { resource: RESOURCES.COMMUNICATIONS.SMS, action: ACTIONS.READ }]} requireAll={false}><LayoutWrapper><Communications /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/communications/logs" element={<ProtectedRoute requiredPermissions={[{ resource: RESOURCES.COMMUNICATIONS.EMAIL, action: ACTIONS.READ }, { resource: RESOURCES.COMMUNICATIONS.SMS, action: ACTIONS.READ }]} requireAll={false}><LayoutWrapper><EmailLogs /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/communications/emails" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.COMMUNICATIONS.EMAIL, action: ACTIONS.SEND }}><LayoutWrapper><EmailManagement /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/communications/sms" element={<ProtectedRoute requiredPermissions={[{ resource: RESOURCES.COMMUNICATIONS.SMS, action: ACTIONS.READ }, { resource: RESOURCES.COMMUNICATIONS.SMS, action: ACTIONS.SEND }]} requireAll={false}><LayoutWrapper><SMSPage /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/teachers" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.TEACHERS, action: ACTIONS.READ }}><LayoutWrapper><Teachers /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/users" element={<ProtectedRoute requiredPermission={{ resource: 'users', action: 'read' }}><LayoutWrapper><UserManagement /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/roles" element={<ProtectedRoute requiredPermission={{ resource: 'roles', action: 'read' }}><LayoutWrapper><Roles /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/school" element={<ProtectedRoute><LayoutWrapper><SchoolSettings /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/account" element={<ProtectedRoute><LayoutWrapper><AccountSettings /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/rbac" element={<ProtectedRoute requiredPermissions={[{ resource: 'permissions', action: 'read' }, { resource: 'users', action: 'read' }]}><LayoutWrapper><RBACPage /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/types" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.TYPES, action: ACTIONS.READ }}><LayoutWrapper><AssessmentTypes /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/configurations" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.CONFIGURATIONS, action: ACTIONS.READ }}><LayoutWrapper><AssessmentConfigurations /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/marks" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.MARKS, action: ACTIONS.READ }}><LayoutWrapper><StudentMarks /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/reports" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.REPORTS, action: ACTIONS.READ }}><LayoutWrapper><AssessmentReports /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/teacher-workload" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.REPORTS, action: ACTIONS.READ }}><LayoutWrapper><TeacherWorkload /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/grading/scales" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.GRADING, action: ACTIONS.READ }}><LayoutWrapper><GradingScales /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/grading/boundaries" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.GRADING, action: ACTIONS.READ }}><LayoutWrapper><GradeBoundaries /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/grading/class-scales" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.GRADING, action: ACTIONS.READ }}><LayoutWrapper><ClassGradingScales /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/hub" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.REPORT_CARDS, action: ACTIONS.READ }}><LayoutWrapper><ReportCardHub /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/conduct" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CONDUCT, action: ACTIONS.READ }}><LayoutWrapper><ConductManagement /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/comments" element={<ProtectedRoute requiredPermissions={[{ resource: RESOURCES.REPORT_CARD.SUBJECT_COMMENTS, action: ACTIONS.READ }, { resource: RESOURCES.REPORT_CARD.GENERAL_COMMENTS, action: ACTIONS.READ }]} requireAll={false}><LayoutWrapper><CommentsManagement /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/conduct-categories" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CONDUCT, action: ACTIONS.READ }}><LayoutWrapper><ConductCategories /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/conduct-ratings" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CONDUCT, action: ACTIONS.READ }}><LayoutWrapper><ConductRatings /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/student-conduct" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CONDUCT, action: ACTIONS.READ }}><LayoutWrapper><StudentConduct /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/subject-comments" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECT_COMMENTS, action: ACTIONS.READ }}><LayoutWrapper><SubjectComments /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/general-comments" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.GENERAL_COMMENTS, action: ACTIONS.READ }}><LayoutWrapper><GeneralComments /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/generation" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.REPORT_CARDS, action: ACTIONS.READ }}><LayoutWrapper><ReportCardGeneration /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/comment-templates" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.COMMENT_TEMPLATES, action: ACTIONS.READ }}><LayoutWrapper><CommentTemplates /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/templates" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.TEMPLATES, action: ACTIONS.READ }}><LayoutWrapper><ReportCardTemplates /></LayoutWrapper></ProtectedRoute>} />
      {/* TODO: Uncomment when PrimaryPreferences component is developed */}
      {/* <Route path="/report-card/primary-preferences" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.PRIMARY_PREFERENCES, action: ACTIONS.READ }}><LayoutWrapper><PrimaryPreferences /></LayoutWrapper></ProtectedRoute>} /> */}

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// Guard for student portal routes
const StudentProtectedRoute = ({ children }) => {
  const { student, loading } = useStudentAuth();
  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;
  if (!student) return <Navigate to="/student/login" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <StudentAuthProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </StudentAuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
